export type AccountStatus =
  | 'incomplete'
  | 'active'
  | 'pending_approval'
  | 'approved'
  | 'rejected'
  | 'deactivated';

export type UserRole = 'applicant' | 'referrer' | 'employer' | 'sponsor' | 'admin' | 'platform_admin' | string;

export interface UserForRedirect {
  role?: string | null;
  status?: AccountStatus | string | null;
  profileCompleted?: boolean;
}

/**
 * Centralized state machine validator for account status transitions.
 */
export function isValidStatusTransition(
  role: string | null | undefined,
  currentStatus: string | null | undefined,
  targetStatus: AccountStatus | string
): boolean {
  let normCurrent = (currentStatus || 'incomplete').toLowerCase();
  if (normCurrent === 'pending') normCurrent = 'pending_approval';
  if (normCurrent === 'active') normCurrent = 'approved';

  let normTarget = (targetStatus || '').toLowerCase();
  if (normTarget === 'active') normTarget = 'approved';

  // Idempotent transitions are always valid
  if (normCurrent === normTarget) return true;

  const roleLower = (role || '').toLowerCase();

  if (roleLower === 'applicant') {
    if (normCurrent === 'incomplete' && (normTarget === 'approved' || normTarget === 'active')) return true;
    if ((normCurrent === 'approved' || normCurrent === 'active') && normTarget === 'deactivated') return true;
    if (normCurrent === 'deactivated' && (normTarget === 'approved' || normTarget === 'active')) return true;
    return false;
  }

  if (['referrer', 'employer', 'sponsor'].includes(roleLower)) {
    if (normCurrent === 'incomplete' && (normTarget === 'pending_approval' || normTarget === 'approved')) return true;
    if (normCurrent === 'pending_approval' && (normTarget === 'approved' || normTarget === 'rejected')) return true;
    if (normCurrent === 'rejected' && normTarget === 'pending_approval') return true;
    if (normCurrent === 'approved' && (normTarget === 'deactivated' || normTarget === 'pending_approval')) return true;
    if (normCurrent === 'deactivated' && normTarget === 'approved') return true;
    // An org parked at 'awaiting_information' (admin requested more info) can
    // still be resolved by approving, rejecting, or deactivating — otherwise
    // the approval 409s and the org is stuck.
    if (normCurrent === 'awaiting_information' && (normTarget === 'approved' || normTarget === 'rejected' || normTarget === 'deactivated')) return true;
    // Legacy 'inactive' (written by the old raw status endpoint) behaves like
    // deactivated so those orgs can be re-approved or deactivated properly.
    if (normCurrent === 'inactive' && (normTarget === 'approved' || normTarget === 'deactivated' || normTarget === 'pending_approval')) return true;
    return false;
  }

  // Admin or platform roles
  if (['admin', 'platform_admin', 'manager', 'owner', 'super', 'master'].includes(roleLower)) {
    return true;
  }

  return false;
}

export interface OrgPendingReviewInput {
  /** Role status API reports an org record exists for this user. */
  onboarded: boolean | null | undefined;
  /** Role status API reports the org is approved/active. null = not yet resolved. */
  approved: boolean | null | undefined;
  /** The org profile still needs fields completed (finish onboarding first). */
  profileIncomplete: boolean | undefined;
  /** Server flagged that org setup is still required. */
  orgSetupRequired?: boolean | undefined;
  /** Explicit rejected status must not count as pending review. */
  roleStatus?: string | null | undefined;
}

/**
 * Pending-review precedence for session resume / dashboard entry.
 *
 * When an organisation profile has been submitted and is awaiting review,
 * the user must land on the PendingApproval screen — regardless of what
 * the stored onboarding state-machine step or profile status says. Without
 * this precedence, a re-login can bounce a submitted user back into the
 * wizard (or a password step) as if the submission never happened.
 */
export function isOrgPendingReview(input: OrgPendingReviewInput): boolean {
  // Unknown approval status (null, check in flight) is NEVER treated as
  // pending — the caller renders the loader until the status resolves.
  // Assuming pending on unknown flashes the "under review" gate for
  // approved accounts.
  if (!input.onboarded || input.approved !== false) return false;
  if (input.profileIncomplete || input.orgSetupRequired) return false;
  const rs = (input.roleStatus || '').toLowerCase();
  if (rs === 'rejected') return false;
  // A deactivated/suspended org is not "pending review" either — it is a
  // terminal state with its own screen (see isOrgTerminalInactiveStatus).
  if (isOrgTerminalInactiveStatus(rs)) return false;
  return true;
}

/**
 * Terminal inactive org statuses (PR-13 fold-in, D-18).
 *
 * When the server's confirmed status for an org role is one of these,
 * the account must land on the "Account Inactive" screen — never the
 * dashboard, never the onboarding wizard, and never the pending-review
 * screen (a deactivated referrer previously fell through to the pending
 * screen because no branch matched its status). 'inactive' is the
 * legacy value written by the old raw status endpoint and behaves like
 * 'deactivated' everywhere else in this module.
 */
export function isOrgTerminalInactiveStatus(status: string | null | undefined): boolean {
  const s = (status || '').toLowerCase();
  return s === 'deactivated' || s === 'suspended' || s === 'disabled' || s === 'inactive';
}

/**
 * PR-13b (product rule change, 2026-10-13 — reverses PR-13's referrer
 * half): a DEACTIVATED REFERRER is not a locked-out account. Referring
 * is restricted; the account itself stays usable (sign-in succeeds,
 * referrer dashboard renders read-only, the applicant role is fully
 * functional). The user-doc deactivation gate in App.tsx must therefore
 * NOT fire when the suspension was sourced by the user's referrer org.
 *
 * True when ALL of these hold:
 * - the user doc carries a deactivation stamp ('deactivated'/'disabled');
 * - the referrer status round reports the referrer org 'deactivated';
 * - the stamp's sourcing org (`suspendedOrgId`), when both it and the
 *   status round's referrer org id are known, IS that referrer org (a
 *   mismatch means a DIFFERENT org — e.g. the user's employer —
 *   sourced the suspension, and the hard lockout stands).
 */
export function isReferrerSourcedSuspension(input: {
  profileStatus: string | null | undefined;
  suspendedOrgId?: string | null | undefined;
  referrerStatus: string | null | undefined;
  referrerOrgId?: string | null | undefined;
}): boolean {
  const ps = (input.profileStatus || '').toLowerCase();
  if (ps !== 'deactivated' && ps !== 'disabled') return false;
  // Pointer proof: the stamp names the user's OWN referrer org (the
  // status round's referrer id, or the user doc's referrerId pointer).
  // That is conclusive even before/without the status round — the
  // round may not have run yet on the role-selection path.
  if (input.suspendedOrgId && input.referrerOrgId && input.suspendedOrgId === input.referrerOrgId) {
    return true;
  }
  if ((input.referrerStatus || '').toLowerCase() !== 'deactivated') return false;
  if (input.suspendedOrgId && input.referrerOrgId && input.suspendedOrgId !== input.referrerOrgId) {
    return false;
  }
  return true;
}

/** Rejected org status (admin declined the submitted org record). */
export function isOrgRejectedStatus(status: string | null | undefined): boolean {
  return (status || '').toLowerCase() === 'rejected';
}

/**
 * Statuses that must NEVER route into the editable onboarding wizard on
 * ANY entry path (role-picker landing, direct /dashboard, session
 * resume): rejected (the admin's decision stands — the user lands on
 * the rejected notice) and the terminal-inactive family (its own
 * screen / restricted routing decides). PR-13b acceptance evidence: a
 * REJECTED referrer reached the rejected notice via the role picker but
 * fell through to the blank editable wizard on direct /dashboard,
 * because the wizard gate consulted only local profile hints
 * (status 'incomplete', orgSetupRequired) and never the confirmed org
 * status. The veto lives here, in the shared gate, so every role and
 * every entry path honors it.
 */
export function isOrgWizardBlockedStatus(status: string | null | undefined): boolean {
  return isOrgRejectedStatus(status) || isOrgTerminalInactiveStatus(status);
}

/**
 * A referrer in restricted mode: onboarded (a real record exists) and
 * its confirmed status is 'deactivated'. Restricted referrers route to
 * their dashboard (read-only + restriction banner) — never the wizard,
 * never the pending screen, never the terminal inactive screen.
 */
export function isReferrerRestricted(input: {
  onboarded: boolean | null | undefined;
  status: string | null | undefined;
}): boolean {
  return input.onboarded === true && (input.status || '').toLowerCase() === 'deactivated';
}

export interface OrgOnboardingGateInput {
  /**
   * Server org status (the role's onboard-status endpoint) says the org is
   * approved/active. null = not yet resolved. This is the SINGLE source of
   * truth for "may this user leave onboarding".
   */
  approved: boolean | null | undefined;
  /** Submitted and awaiting review (see isOrgPendingReview). */
  pendingReview: boolean;
  /**
   * Profile/local-derived signals that would normally route this user into
   * the onboarding wizard (user-doc status 'incomplete', onboarded flag
   * false, orgSetupRequired, incomplete org profile). These are hints only —
   * they may describe a stale USER document and must never outrank the
   * server org status.
   */
  needsOnboarding: boolean;
  /**
   * The role's raw confirmed org status (e.g. 'rejected', 'deactivated'),
   * when the caller has it. A rejected or terminal-inactive status vetoes
   * the wizard outright (see isOrgWizardBlockedStatus) — on every entry
   * path, regardless of the local hints in `needsOnboarding`.
   */
  roleStatus?: string | null | undefined;
}

/**
 * Deterministic post-login routing for org roles (Master Plan §1.1).
 *
 * The onboarding wizard renders ONLY when this returns true. The rule that
 * makes landing deterministic: an org the server reports as APPROVED can
 * never be routed into the wizard, no matter what the user document or any
 * cached/local signal says. Previously the wizard branches consulted the
 * user-doc fields (`status: 'incomplete'`, `orgSetupRequired`) and the
 * onboarded flag WITHOUT an approval veto on most of them, so an approved
 * referrer/employer/sponsor whose user doc still carried a stale
 * 'incomplete' / orgSetupRequired flag — or whose onboard flag lagged —
 * landed on the wizard (sometimes on every login, sometimes intermittently,
 * depending on which stale field that account's doc happened to carry),
 * and the wizard branch replaced the whole shell regardless of the URL
 * (e.g. /candidates rendered wizard content). With the veto, server org
 * status decides: approved → dashboard, pending → pending gate, genuinely
 * unsubmitted (approved === false with onboarding signals) → wizard.
 */
export function orgOnboardingGate(input: OrgOnboardingGateInput): boolean {
  // UNRESOLVED approval (null/undefined — the status round has not
  // produced a server answer for this role) must NEVER route into the
  // wizard. PR-10: the previous contract passed local signals through on
  // null, trusting orgRoleBranchesReady to keep unresolved state out of
  // the branches — but the session-global resolved flag is set by rounds
  // that never fetched this role's status (role detected after the round
  // ran), by the token catch, and by the hang fallback, so an approved
  // referrer whose status fetch merely lagged or raced role detection
  // fell through to the permanently-true user-doc hints
  // (status: 'incomplete') and landed on the wizard — intermittently,
  // depending on which fetch won the race on that load. Only an explicit
  // server answer (approved === false) may route to the wizard; callers
  // render the loader/error branch while approval is unresolved.
  if (input.approved === null || input.approved === undefined) return false;
  // Server approval outranks every local/profile signal.
  if (input.approved === true) return false;
  // A confirmed REJECTED or terminal-inactive status outranks the local
  // hints too: the wizard is an editable re-application surface, and a
  // rejected/terminal org must land on its decision screen instead —
  // on the role-picker path AND on direct /dashboard alike (PR-13b).
  if (isOrgWizardBlockedStatus(input.roleStatus)) return false;
  // Submitted users belong on the pending gate, not back in the wizard.
  if (input.pendingReview) return false;
  return input.needsOnboarding;
}

/**
 * "Loading your roles" gate readiness (PR-13b).
 *
 * The full-screen role loader must only wait for the data the NEXT
 * screen actually needs. Previously it required the referrer tier
 * (referrerEnhanced) to resolve for EVERY referrer session — including
 * users whose role was already settled (single-role accounts, a stored
 * session choice, direct /dashboard navigation) — so a status round
 * that lagged, raced role detection, or never applied left the session
 * parked on the spinner indefinitely, even though the dashboard branch
 * never consumes the tier to decide routing. Rule: role options must
 * be resolved; the tier is additionally required ONLY when the role
 * SELECTION screen is about to render (its option list depends on the
 * tier); a failed status round releases the gate (the branches render
 * their own failure state).
 */
export function roleScreenReady(input: {
  roleOptionsResolved: boolean;
  roleSelectionNeeded: boolean;
  isReferrer: boolean;
  referrerTierResolved: boolean;
  roleStatusFailed: boolean;
}): boolean {
  if (!input.roleOptionsResolved) return false;
  if (!input.roleSelectionNeeded) return true;
  if (input.roleStatusFailed) return true;
  if (input.isReferrer && !input.referrerTierResolved) return false;
  return true;
}

/**
 * Post-auth route gating for org roles.
 *
 * The org-role branches (onboarding wizard / pending-approval / dashboard)
 * must not render until the role-status check has resolved for the current
 * session. The role state starts as `null` (and is reset to `null` on
 * logout), so without this gate a re-login renders the wizard from stale
 * state while the status fetch is in flight — and if that fetch is slow,
 * aborted, or fails, a submitted user gets stranded mid-onboarding as if
 * their submission never happened. Gating suspends the transition on a
 * loader until the check resolves, so a pending-approval user lands
 * directly on PendingApproval with no reload and never sees the wizard.
 */
export function orgRoleBranchesReady(input: {
  isEmployer: boolean;
  isReferrer: boolean;
  isSponsor: boolean;
  roleStatusResolved: boolean;
  /**
   * Per-role server approval from the status round (null = the server
   * has not answered for this role yet). PR-10: `roleStatusResolved`
   * alone is NOT sufficient — it is a session-global flag set by ANY
   * completed round, including rounds that ran before the role was
   * detected (and therefore never fetched that role's status), the
   * getIdToken catch, and the hang fallback. A role whose approval is
   * still explicitly null has not resolved: the branches must stay
   * suspended on the loader, because rendering them lets
   * orgOnboardingGate's local user-doc hints route an approved user
   * into the onboarding wizard (intermittent wizard-vs-dashboard
   * flip-flop across loads). Callers that do not track a role's
   * approval pass undefined, which does not block (legacy behavior).
   */
  employerApproved?: boolean | null;
  referrerApproved?: boolean | null;
  /**
   * The status round failed (status UNKNOWN). The caller renders a
   * dedicated error branch (loader + Retry) in that case, so the
   * branches count as "ready" — otherwise the error UI is unreachable
   * behind this loader.
   */
  roleStatusFailed?: boolean;
}): boolean {
  const isOrgRole = input.isEmployer || input.isReferrer || input.isSponsor;
  if (!isOrgRole) return true;
  if (!input.roleStatusResolved) return false;
  if (input.roleStatusFailed) return true;
  if (input.isEmployer && input.employerApproved === null) return false;
  if (input.isReferrer && input.referrerApproved === null) return false;
  return true;
}

/**
 * PR-13 — trust model for onboard-status answers (D-9 residual).
 *
 * PR-10 stopped the client from routing on an UNRESOLVED state, but a
 * single WRONG answer was still sufficient and unvetoed: when the
 * server's referrer lookup glitched (a swallowed Firestore exception, a
 * stale per-instance negative cache), /api/referrer/onboard-status
 * answered `{ onboarded: false }` for an approved referrer, the client
 * believed it, and the onboarding wizard flashed on every affected
 * sign-in until a later round corrected it (live: 3/3 sign-ins on the
 * PR-10 build, flips at ~9s–~1min).
 *
 * This state machine is the client half of the fix. The server half
 * (PR-13) makes lookup failures surface as errors instead of negatives,
 * but the client must not stake a routing decision on ONE negative
 * answer regardless of what produced it:
 *
 * - POSITIVE answers (an org record exists) apply immediately and are
 *   STICKY for the session: once any round has reported onboarded, no
 *   later negative round may route the user to the wizard or the
 *   pending screen — the best-known positive state wins.
 * - NEGATIVE answers (no record) are UNCONFIRMED until at least
 *   {@link ORG_NEGATIVE_CONFIRM_MIN_ROUNDS} consecutive answered rounds
 *   agree AND the streak spans at least
 *   {@link ORG_NEGATIVE_CONFIRM_MIN_SPAN_MS}. Until confirmed, the
 *   caller keeps the last known routing state (on a fresh session:
 *   unresolved, so the branches stay on the loader) and re-checks
 *   promptly. A lone negative never ages into a wizard on its own —
 *   that is precisely the observed failure (one stale answer, slow
 *   follow-up rounds).
 * - FAILED rounds (HTTP/network error) are NEUTRAL: they neither count
 *   toward a negative nor reset a streak, and never apply — the
 *   caller's existing failure handling (loader + Retry, PR-10 sticky
 *   resolution) governs them.
 */
export interface OrgStatusTrustState {
  /** Any answered round this session reported an org record exists. Sticky. */
  sawOnboarded: boolean;
  /** Consecutive answered NEGATIVE rounds in the current streak. */
  consecutiveNegatives: number;
  /** Timestamp (ms) of the first negative in the current streak, if any. */
  firstNegativeAt: number | null;
}

/** Consecutive agreeing negative rounds required before a negative is trusted. */
export const ORG_NEGATIVE_CONFIRM_MIN_ROUNDS = 2;
/** Minimum span (ms) of the negative streak before it is trusted. */
export const ORG_NEGATIVE_CONFIRM_MIN_SPAN_MS = 4_000;
/** How soon (ms) the caller should re-check while a negative is unconfirmed. */
export const ORG_NEGATIVE_RECHECK_MS = 2_000;

export function createOrgStatusTrustState(): OrgStatusTrustState {
  return { sawOnboarded: false, consecutiveNegatives: 0, firstNegativeAt: null };
}

export type OrgStatusRoundOutcome = 'positive' | 'negative' | 'failed';

/** Fold one completed status round into the trust state (pure). */
export function applyOrgStatusRound(
  state: OrgStatusTrustState,
  outcome: OrgStatusRoundOutcome,
  now: number,
): OrgStatusTrustState {
  if (outcome === 'failed') return state;
  if (outcome === 'positive') {
    return { sawOnboarded: true, consecutiveNegatives: 0, firstNegativeAt: null };
  }
  // Negative answer.
  if (state.sawOnboarded) return state; // sticky positive vetoes negatives
  return {
    sawOnboarded: false,
    consecutiveNegatives: state.consecutiveNegatives + 1,
    firstNegativeAt: state.firstNegativeAt ?? now,
  };
}

/** Is the current negative streak strong enough to route on? */
export function isOrgNegativeConfirmed(state: OrgStatusTrustState, now: number): boolean {
  if (state.sawOnboarded) return false;
  if (state.consecutiveNegatives < ORG_NEGATIVE_CONFIRM_MIN_ROUNDS) return false;
  if (state.firstNegativeAt === null) return false;
  return now - state.firstNegativeAt >= ORG_NEGATIVE_CONFIRM_MIN_SPAN_MS;
}

export interface OrgStatusRoundResolution {
  /** Next trust state — the caller stores this regardless of `apply`. */
  state: OrgStatusTrustState;
  /**
   * May this round's answer be applied to the routing state?
   * Positives always apply; negatives only once confirmed; failed
   * rounds never apply (status unknown, not negative).
   */
  apply: boolean;
  /**
   * A negative was seen but is not yet confirmed — the caller should
   * schedule a prompt re-check (ORG_NEGATIVE_RECHECK_MS) so a genuine
   * negative confirms within seconds, without waiting for the slow
   * background poll.
   */
  needsRecheck: boolean;
}

/** Resolve one completed status round against the trust state (pure). */
export function resolveOrgStatusRound(
  state: OrgStatusTrustState,
  outcome: OrgStatusRoundOutcome,
  now: number,
): OrgStatusRoundResolution {
  const next = applyOrgStatusRound(state, outcome, now);
  if (outcome === 'positive') return { state: next, apply: true, needsRecheck: false };
  if (outcome === 'failed') return { state: next, apply: false, needsRecheck: false };
  const confirmed = isOrgNegativeConfirmed(next, now);
  return { state: next, apply: confirmed, needsRecheck: !confirmed && !next.sawOnboarded };
}

/**
 * Decides landing view / route based on user's role and status.
 *
 * Outcomes:
 * - No role -> 'role-selection'
 * - incomplete -> 'profile-completion'
 * - pending_approval -> 'pending-approval'
 * - rejected -> 'rejected'
 * - approved or Applicant active -> 'dashboard'
 * - deactivated -> 'deactivated'
 */
export function getPostAuthRedirect(user: UserForRedirect | null | undefined): string {
  if (!user || !user.role) {
    return '/select-role';
  }

  const role = user.role.toLowerCase();
  const status = (user.status || (user.profileCompleted ? 'active' : 'incomplete')).toLowerCase();

  if (status === 'deactivated') {
    return '/deactivated';
  }

  if (['admin', 'platform_admin', 'manager', 'owner', 'super', 'master'].includes(role)) {
    return '/dashboard';
  }

  if (role === 'applicant') {
    if (status === 'incomplete' && !user.profileCompleted) {
      return '/profile-completion';
    }
    return '/dashboard';
  }

  if (['referrer', 'employer', 'sponsor'].includes(role)) {
    if (status === 'incomplete') {
      return '/profile-completion';
    }
    if (status === 'pending_approval') {
      return '/pending-approval';
    }
    if (status === 'rejected') {
      return '/rejected';
    }
    if (status === 'approved' || status === 'active') {
      return '/dashboard';
    }
    return '/pending-approval';
  }

  return '/dashboard';
}
